// client/src/pages/secure/TrainingRecordPage.js — one person's training record, printable,
// for the file or a Division examiner: in-house training (with attestation and quiz score)
// and external CE by licence cycle. Without :type/:id it lists everyone (supervisors and
// examiners granted "Licensee training records").
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import { Notice, fetchBlob, fmtDate, openBlob } from '../../components/secure/training/shared';
import { EnrolForm } from '../../components/secure/training/Program';
import { getCurrentUser } from '../../hooks/useCurrentUser';
import DataList from '../../components/shared/DataList';

const Roster = () => {
    const api = useApiClient();
    const [d, setD] = useState(null);
    const [error, setError] = useState('');
    useEffect(() => { api.get('/api/training/records').then(setD).catch((e) => setError(e.message)); }, [api]);
    if (!d) return error ? <Notice tone="bad">{error}</Notice> : <p>Loading…</p>;
    return (
        <div>
            <h1 className="text-2xl font-bold text-slate-800 mb-2">Licensee training records</h1>
            <p className="text-sm text-slate-600 mb-4">{d.disclaimer}</p>
            <div className="bg-white rounded shadow p-3"><DataList rows={d.people} rowKey={(p) => `${p.person_type}:${p.person_id}`} csvName="training-records" defaultSort={{ key: 'name', dir: 'asc' }}
                filters={[{ key: 'person_type', label: 'Type' }, { key: 'status', label: 'Status' }]}
                columns={[
                    { key: 'name', label: 'Name' },
                    { key: 'person_type', label: 'Type' },
                    { key: 'status', label: 'Status' },
                    { key: 'completed', label: 'Completed', type: 'number' },
                    { key: 'overdue', label: 'Overdue', type: 'number' },
                    { key: 'licences', label: 'Licences', type: 'number' },
                    { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (p) => <Link className="text-indigo-700 underline" to={`/dashboard/training/record/${p.person_type}/${p.person_id}`}>Open record</Link> },
                ]} /></div>
        </div>
    );
};

const TrainingRecordPage = () => {
    const { type, id } = useParams();
    const api = useApiClient();
    const [d, setD] = useState(null);
    const [error, setError] = useState('');
    const load = useCallback(async () => {
        try { setD(await api.get(`/api/training/records/${type}/${id}`)); } catch (e) { setError(e.message); }
    }, [api, type, id]);
    useEffect(() => { if (type && id) load(); }, [load, type, id]);
    if (!type || !id) return <Roster />;
    const u = getCurrentUser();
    const supervisor = !!u && u.role !== 'examiner' && !!(u.isManagingBroker || u.staffRole === 'Office Manager');
    if (!d) return error ? <Notice tone="bad">{error}</Notice> : <p>Loading…</p>;
    const cert = async (c) => {
        try { openBlob(await fetchBlob(`/api/training/records/${type}/${id}/certificates/${c.ce_id}`), `certificate-${c.ce_id}`); } catch (e) { setError(e.message); }
    };
    return (
        <div className="bg-white p-6 rounded shadow print:shadow-none print:p-0 max-w-5xl">
            <div className="flex justify-between items-start mb-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Training record — {d.person.name}</h1>
                    <p className="text-sm text-slate-600">Business Real Estate Corp. · {d.person.person_type}{d.person.licence_number ? ` · licence ${d.person.licence_number}` : ''} · generated {new Date(d.generated_at).toLocaleString()}</p>
                </div>
                <button type="button" onClick={() => window.print()} className="print:hidden px-3 py-1.5 rounded bg-slate-800 text-white text-sm">Print</button>
            </div>
            {error && <Notice tone="bad">{error}</Notice>}
            <p className="text-xs text-slate-600 mb-4 border-l-4 border-slate-300 pl-2">{d.disclaimer}</p>

            {supervisor && (
                <details className="print:hidden border rounded p-3 mb-4">
                    <summary className="font-semibold cursor-pointer text-sm">90-day programme: enrol {d.person.name}</summary>
                    <div className="mt-2"><EnrolForm person={{ person_type: d.person.person_type, person_id: d.person.person_id }} /></div>
                    <p className="text-xs text-slate-500">Progress, mentor and check-ins: Training → Programme overview.</p>
                </details>
            )}
            <h2 className="text-lg font-semibold mt-4 mb-2">In-house training (supervision record, 68 IAC 1450.705(b))</h2>
            <DataList rows={d.in_house.map((a, i) => ({ ...a, _key: i }))} rowKey="_key" csvName="in-house-training" defaultSort={{ key: 'assigned_at', dir: 'desc' }} empty="No in-house training recorded."
                filters={[{ key: 'status', label: 'Status' }]}
                columns={[
                    { key: 'course', label: 'Course', value: (a) => `${a.course}${a.content_version ? ` v${a.content_version}` : ''}`, sortValue: (a) => a.course,
                        render: (a) => <>{a.course}{a.content_version ? <span className="text-xs text-slate-500"> v{a.content_version}</span> : null}</> },
                    { key: 'assigned_at', label: 'Assigned', type: 'date', render: (a) => fmtDate(a.assigned_at) },
                    { key: 'due_date', label: 'Due', type: 'date', render: (a) => fmtDate(a.due_date) },
                    { key: 'status', label: 'Status', render: (a) => `${a.status}${a.waived_reason ? ` (${a.waived_reason})` : ''}` },
                    { key: 'completed_at', label: 'Completed', type: 'date', render: (a) => fmtDate(a.completed_at) },
                    { key: 'completed_via', label: 'How', render: (a) => a.completed_via || '' },
                    { key: 'quiz_score', label: 'Quiz', type: 'number', render: (a) => (a.quiz_score != null ? `${a.quiz_score}%` : '') },
                    { key: 'attestation', label: 'Attestation', render: (a) => <span className="text-xs">{a.attestation || ''}</span> },
                ]} />

            <h2 className="text-lg font-semibold mt-6 mb-2">Licences and external continuing education</h2>
            {!d.licences.length && <p className="text-sm text-slate-500">No licence recorded in the CE tracker.</p>}
            {d.licences.map((l) => (
                <div key={l.licence_id} className="mb-4 break-inside-avoid">
                    <div className="font-medium">{l.state} {String(l.licence_type).replace(/_/g, ' ')}{l.licence_number ? ` · ${l.licence_number}` : ''} — cycle {fmtDate(l.cycle_start)} to {fmtDate(l.cycle_end)} ({l.status}){Number(l.is_example) === 1 ? ' — example record' : ''}</div>
                    <div className="text-sm text-slate-700">{l.rule ? `${l.rule.label} — ${l.rule.authority}` : 'No CE rule'} · {l.standing.message}</div>
                    <div className="mt-1"><DataList rows={l.courses} rowKey="ce_id" csvName={`ce-courses-${l.licence_id}`} defaultSort={{ key: 'completed_on', dir: 'desc' }} empty="No courses recorded for this cycle."
                        filters={[{ key: 'category', label: 'Category' }]}
                        columns={[
                            { key: 'completed_on', label: 'Completed', type: 'date', render: (c) => fmtDate(c.completed_on) },
                            { key: 'course_title', label: 'Course' },
                            { key: 'course_number', label: 'Course no.', render: (c) => c.course_number || '' },
                            { key: 'provider', label: 'Provider', value: (c) => `${c.provider || ''}${c.provider_number ? ` (${c.provider_number})` : ''}` },
                            { key: 'category', label: 'Category' },
                            { key: 'hours', label: 'Hours', type: 'number', value: (c) => Number(c.hours) },
                            { key: 'certificate', label: 'Certificate', value: (c) => `${c.has_certificate ? 'on file' : 'not uploaded'}${c.verified_by_name ? ` · verified by ${c.verified_by_name}` : ''}`,
                                render: (c) => <>{c.has_certificate ? <button type="button" className="text-indigo-700 underline print:no-underline" onClick={() => cert(c)}>on file</button> : 'not uploaded'}{c.verified_by_name ? ` · verified by ${c.verified_by_name}` : ''}</> },
                        ]} /></div>
                </div>
            ))}
        </div>
    );
};

export default TrainingRecordPage;
